import React, { useState, useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  KimeraVisionContainer,
  BackgroundImage,
  OffScreenImage,
  TVContainer,
  TVImage,
  VideoOverlay,
  ControlsContainer,
  ButtonGroup,
  ControlButton,
  InactiveControlButton,
} from './KimeraVision.styles';
import { KimeraVisionProps, Channel, KimeraVisionRef } from './KimeraVision.types';
import { useI18n } from '../../hooks/useI18n';
import televisorImage from '../../assets/kimeraware_televisor.png';
import rectanglesImage from '../../assets/Rectangulos_KW.png';
import offScreenImage from '../../assets/off_Screen.png';
import staticVideo from '../../assets/video/static.mp4';
import catervaVideo from '../../assets/video/caterva_trailer.mp4';
import sanMartinVideo from '../../assets/video/san_martin_trailer.mp4';
import tumamaVideo from '../../assets/video/tumama.mp4';
import onButton from '../../assets/controls/ON.png';
import offButton from '../../assets/controls/OFF.png';
import upButton from '../../assets/controls/ARRIBA.png';
import downButton from '../../assets/controls/abajo.png';
import muteButton from '../../assets/controls/MUTE.png';
import soundButton from '../../assets/controls/SOUND.png';
import steamButton from '../../assets/controls/STEAM.png';
import steamOff from '../../assets/controls/steam_apagado.png';

const KimeraVision = React.memo(
  forwardRef<KimeraVisionRef, KimeraVisionProps>(({ className }, ref) => {
    const { t } = useI18n();
    const channels: Channel[] = useMemo(
      () => [
        { video: staticVideo },
        { video: catervaVideo, steampage: 'http://s.team/a/5012060' },
        { video: sanMartinVideo, steampage: 'http://s.team/a/5023240' },
      ],
      [],
    );
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isTVOn, setIsTVOn] = useState(true);
    const [isSoundOn, setIsSondOn] = useState(false);
    const [isEasterEggActive, setIsEasterEggActive] = useState(false);
    const [easterEggVideo, setEasterEggVideo] = useState<string>(tumamaVideo);

    useImperativeHandle(ref, () => ({
      triggerEasterEgg: (videoPath: string) => {
        setEasterEggVideo(videoPath);
        setIsEasterEggActive(true);
        setIsTVOn(true);
        setIsSondOn(true);
      },
    }));

    const handleSteamClick = () => {
      const currentChannel = channels[currentVideoIndex];
      if (currentChannel.steampage) {
        window.open(currentChannel.steampage, '_blank');
      }
    };

    const handleUpButton = () => {
      if (isEasterEggActive) {
        setIsEasterEggActive(false);
      }
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % channels.length);
    };

    const handleDownButton = () => {
      if (isEasterEggActive) {
        setIsEasterEggActive(false);
      }
      setCurrentVideoIndex((prevIndex) => (prevIndex - 1 + channels.length) % channels.length);
    };

    const handleVideoEnded = () => {
      if (isEasterEggActive) {
        setIsEasterEggActive(false);
      }
    };

    const soundMuteButton = isSoundOn ? 'mute' : 'sound';
    const onoffButton = isTVOn ? 'off' : 'on';

    const currentVideo = isEasterEggActive ? easterEggVideo : channels[currentVideoIndex].video;

    return (
      <KimeraVisionContainer className={className}>
        <BackgroundImage src={rectanglesImage} alt="" aria-hidden="true" />

        <TVContainer>
          <OffScreenImage src={offScreenImage} alt="" aria-hidden="true" />
          {isTVOn ? (
            <VideoOverlay
              src={currentVideo}
              autoPlay
              loop={!isEasterEggActive}
              muted={!isSoundOn}
              playsInline
              aria-hidden="true"
              onEnded={handleVideoEnded}
            />
          ) : (
            <></>
          )}
          <TVImage src={televisorImage} alt="Retro TV" aria-label="Retro TV" />
          <ControlsContainer>
            <ButtonGroup>
              <ControlButton
                src={isTVOn ? offButton : onButton}
                alt={t('kimeraVision.controls.on')}
                aria-label={t(`kimeraVision.controls.${onoffButton}`)}
                onClick={() => setIsTVOn(!isTVOn)}
              />
              <ControlButton
                src={upButton}
                alt={t('kimeraVision.controls.up')}
                aria-label={t('kimeraVision.controls.up')}
                onClick={handleUpButton}
              />
              <ControlButton
                src={downButton}
                alt={t('kimeraVision.controls.down')}
                aria-label={t('kimeraVision.controls.down')}
                onClick={handleDownButton}
              />
            </ButtonGroup>
            <ButtonGroup>
              <ControlButton
                src={isSoundOn ? soundButton : muteButton}
                alt={t('kimeraVision.controls.mute')}
                aria-label={t(`kimeraVision.controls.${soundMuteButton}`)}
                onClick={() => setIsSondOn(!isSoundOn)}
              />
              {channels[currentVideoIndex].steampage && isTVOn && !isEasterEggActive ? (
                <ControlButton
                  src={steamButton}
                  alt={t('kimeraVision.controls.steam')}
                  aria-label={t('kimeraVision.controls.steam')}
                  onClick={handleSteamClick}
                />
              ) : (
                <InactiveControlButton
                  src={steamOff}
                  alt={t('kimeraVision.controls.steam')}
                  aria-label={t('kimeraVision.controls.steam')}
                />
              )}
            </ButtonGroup>
          </ControlsContainer>
        </TVContainer>
      </KimeraVisionContainer>
    );
  }),
);

KimeraVision.displayName = 'KimeraVision';

export default KimeraVision;
